import type { OrderDetails } from '@/entities/order/model'
import type { RestrictedCategory } from '@/features/order/model'
import { getVanSeq } from '@/entities/order/lib'
import classes from './OrderDetailsMetaHeader.module.css'

interface OrderDetailsMetaHeaderProps {
  order: OrderDetails
  restrictedCategories: RestrictedCategory[]
}

/**
 * One glyph per detected category, in the design's order. Area codes already tell us
 * exactly which categories are present, so the pill is composed from them rather than
 * switching between a per-combination set of images.
 */
const CATEGORY_INDICATORS: { category: RestrictedCategory, emoji: string, label: string }[] = [
  { category: 'liquor', emoji: '🍷', label: 'Liquor' },
  { category: 'tobacco', emoji: '🚬', label: 'Tobacco' },
]

function RestrictedItemIndicator({ restrictedCategories }: Pick<OrderDetailsMetaHeaderProps, 'restrictedCategories'>) {
  const indicators = CATEGORY_INDICATORS.filter(({ category }) => restrictedCategories.includes(category))

  return (
    <span
      className={classes.restrictedItemBadge}
      data-restricted-item-variant={indicators.map(({ category }) => category).join('-')}
    >
      {indicators.map(({ category, emoji, label }) => (
        <span key={category} aria-label={label} className={classes.categoryIcon} role="img">
          {emoji}
        </span>
      ))}
    </span>
  )
}

export function OrderDetailsMetaHeader({ order, restrictedCategories }: OrderDetailsMetaHeaderProps) {
  const { vanNumber, sequenceNumber } = getVanSeq(order.hsFields)
  const orderNumber = order.orderId.startsWith('#') ? order.orderId : `#${order.orderId}`

  return (
    <div className={classes.header} data-testid="order-details-meta-header">
      <span className={classes.orderNumber}>{orderNumber}</span>

      <RestrictedItemIndicator restrictedCategories={restrictedCategories} />

      <div className={classes.vehicleDetails}>
        {vanNumber && (
          <span className={classes.vehicleDetail}>
            <span className={classes.vehicleLabel}>Van</span>
            <span className={classes.vehicleValue}>{vanNumber}</span>
          </span>
        )}

        {sequenceNumber && (
          <span className={classes.vehicleDetail}>
            <span className={classes.vehicleLabel}>Seq</span>
            <span className={classes.vehicleValue}>{sequenceNumber}</span>
          </span>
        )}
      </div>
    </div>
  )
}
